"use client";

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ContentfulBrand, formatContentfulImageUrl } from '@/lib/contentful';
import { prepareGoogleFaviconDomain } from '@/lib/icons';
import styles from './brands.module.css';
import BrandLogo from '@/components/ui/BrandLogo';
import Script from 'next/script';
import { generatePersonSchema } from '@/lib/structuredData';
import ContactForm from '@/components/ui/ContactForm';
import Icon from '@/components/ui/Icon';
import { projects as portfolio } from '@/lib/projects';

// Article type definition
interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  publishDate: string;
  featuredImage?: any;
}

// Props interface for the client component
interface HomePageClientProps {
  nonce: string;
}

// Make the component accept nonce as prop
export default function HomePageClient({ nonce }: HomePageClientProps) {
  const [latestArticles, setLatestArticles] = useState<Article[]>([]);
  const [loadingArticles, setLoadingArticles] = useState<boolean>(true);
  const [brands, setBrands] = useState<ContentfulBrand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState<boolean>(true);

  // Generate person schema for the homepage
  const personSchema = generatePersonSchema();

  // Add this function to handle smooth scrolling to sections
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, sectionId: string) => {
    e.preventDefault();
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    async function fetchLatestArticles() {
      try {
        const response = await fetch('/api/articles/latest');
        if (response.ok) {
          const data = await response.json();
          setLatestArticles(data);
        }
      } catch (error) {
        console.error('Error fetching latest articles:', error);
      } finally {
        setLoadingArticles(false); // Use renamed state setter
      }
    }

    async function fetchBrandsFromAPI() {
      try {
        const response = await fetch('/api/brands');
        if (response.ok) {
          const contentfulBrands = await response.json();
          // Sort brands by year descending (newest first)
          const sorted = [...contentfulBrands].sort((a: ContentfulBrand, b: ContentfulBrand) => {
            const yearA = parseInt((a.yearWorked || '0').match(/\d{4}/)?.[0] || '0');
            const yearB = parseInt((b.yearWorked || '0').match(/\d{4}/)?.[0] || '0');
            // For ranges like "2022-Present", extract end year; treat "Present" as 9999
            const endA = a.yearWorked?.includes('Present') ? 9999 : parseInt((a.yearWorked || '0').split(/[-–—]/).pop()?.match(/\d{4}/)?.[0] || String(yearA));
            const endB = b.yearWorked?.includes('Present') ? 9999 : parseInt((b.yearWorked || '0').split(/[-–—]/).pop()?.match(/\d{4}/)?.[0] || String(yearB));
            // Sort by end year desc, then start year desc
            if (endB !== endA) return endB - endA;
            return yearB - yearA;
          });
          setBrands(sorted);
        } else {
          console.error('Error fetching brands from API:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setLoadingBrands(false);
      }
    }
    
    fetchLatestArticles();
    fetchBrandsFromAPI();
  }, []);

  // Format date helper
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  {/* Hero Section - Option 4 (Witty, modern & eye-catching version) */}
  const renderHeroOption4 = () => (
    <section className="hero-section hero-option-4" id="home">
      <div className="gradient-blob"></div>
      <div className="grid-pattern"></div>
      
      <div className="container hero-container-4">
        <div className="hero-grid">
          <div className="hero-content-modern">
            <div className="headline-wrapper">
              <span className="hero-eyebrow">DTC Growth Strategy · Performance Marketing · Revenue Optimization</span>
              <div className="hero-dynamic-text">
                <h1 className="hero-title-dynamic">
                  <span className="title-static">I scale DTC brands through</span>
                  <span className="title-changing">
                    <span className="title-word">performance</span>
                    <span className="title-word">acquisition</span>
                    <span className="title-word">retention</span>
                    <span className="title-word">revenue</span>
                  </span>
                </h1>
              </div>
            </div>

            <p className="hero-description-modern">
              25+ years building revenue engines for the world&apos;s most ambitious brands. I connect
              performance marketing to commercial outcomes, and hold every dollar accountable.
            </p>

            <div className="hero-cta-modern">
              <Link href="#contact" className="btn btn-primary" onClick={(e) => scrollToSection(e, 'contact')}>
                See how I drive growth
              </Link>
              <Link href="/experience" className="btn-neon">
                <span>View my experience</span>
                <Icon name="arrowRight" size={18} />
              </Link>
            </div>
          </div>

          <figure className="hero-showcase">
            <a
              href="https://cleanon.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="showcase-link"
              aria-label="Visit CleanOn, my latest project (opens in a new tab)"
            >
              <div className="showcase-browser">
                <div className="showcase-bar" aria-hidden="true">
                  <span className="showcase-dots"><i /><i /><i /></span>
                  <span className="showcase-url">cleanon.com.au</span>
                </div>
                <Image
                  src="/work/cleanon-desktop.webp"
                  alt="CleanOn homepage on desktop"
                  width={1600}
                  height={1000}
                  priority
                  sizes="(max-width: 992px) 92vw, 640px"
                  className="showcase-shot"
                />
              </div>
              <div className="showcase-phone" aria-hidden="true">
                <Image
                  src="/work/cleanon-mobile.webp"
                  alt=""
                  width={600}
                  height={1298}
                  sizes="160px"
                />
              </div>
            </a>
            <figcaption className="showcase-caption">
              <span className="showcase-label">Latest project</span>
              <a href="https://cleanon.com.au" target="_blank" rel="noopener noreferrer" className="showcase-title">
                CleanOn <Icon name="arrowUpRight" size={16} />
              </a>
              <span className="showcase-meta">Sydney cleaning booking platform</span>
            </figcaption>
          </figure>
        </div>

        <div className="achievement-cards">
          <div className="achievement-card">
            <div className="achievement-content">
              <div className="achievement-number">$100M+</div>
              <div className="achievement-text">Revenue influenced across<br />DTC &amp; enterprise brands</div>
            </div>
          </div>
          <div className="achievement-card">
            <div className="achievement-content">
              <div className="achievement-number">25+</div>
              <div className="achievement-text">Years scaling brands from<br />startup to market leader</div>
            </div>
          </div>
          <div className="achievement-card">
            <div className="achievement-content">
              <div className="achievement-number">50+</div>
              <div className="achievement-text">Brands across DTC,<br />marketplace &amp; enterprise</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  return (
    <>
      {/* Add JSON-LD structured data */}
      <Script
        id="person-structured-data"
        type="application/ld+json"
        nonce={nonce}
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      
      {/* Homepage WebSite Schema */}
      <Script
        id="website-structured-data"
        type="application/ld+json"
        nonce={nonce}
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "url": "https://bemoi.com",
            "name": "BEMOI | DTC Growth & Performance Marketing Leader",
            "description": "DTC growth strategist and performance marketing leader. 25+ years driving commercial outcomes for the world's most ambitious brands.",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://bemoi.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })
        }}
      />
      <main>
        {/* Hero */}
        {renderHeroOption4()}

        {/* Recent websites */}
        <section className="portfolio" id="work">
          <div className="container">
            <div className="portfolio-head">
              <div>
                <span className="portfolio-eyebrow">Recent work</span>
                <h2 className="portfolio-title">Websites I&apos;ve recently built</h2>
              </div>
              <div className="portfolio-intro">
                <p>Alongside growth strategy, I design and build websites and web apps that are fast, SEO-ready and built to convert.</p>
                <Link href="/projects" className="portfolio-all">
                  Web design &amp; development <Icon name="arrowRight" size={16} />
                </Link>
              </div>
            </div>
            <div className="portfolio-grid">
              {portfolio.map((site) => (
                <a
                  key={site.domain}
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="portfolio-card"
                  aria-label={`${site.name}: ${site.domain} (opens in a new tab)`}
                >
                  <div className="portfolio-browser">
                    <div className="portfolio-bar" aria-hidden="true">
                      <span className="showcase-dots"><i /><i /><i /></span>
                      <span className="portfolio-url">{site.domain}</span>
                    </div>
                    <div className="portfolio-shot">
                      <Image
                        src={site.image}
                        alt={`${site.name} homepage`}
                        width={1600}
                        height={1000}
                        sizes="(max-width: 768px) 92vw, 560px"
                      />
                    </div>
                  </div>
                  <div className="portfolio-meta">
                    <div>
                      <span className="portfolio-category">{site.category}</span>
                      <h3 className="portfolio-name">{site.name}</h3>
                    </div>
                    <span className="portfolio-arrow" aria-hidden="true">
                      <Icon name="arrowUpRight" size={18} />
                    </span>
                  </div>
                  <p className="portfolio-summary">{site.summary}</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* DTC & E-Commerce Focus Section */}
        <section className="dtc-focus" id="dtc-focus">
          <div className="container">
            <h2 className="section-title">What I Do</h2>
            <p className="section-subtitle">I help DTC and e-commerce brands turn marketing spend into predictable, profitable revenue.</p>
            <div className="dtc-cards-grid">
              <div className="dtc-card">
                <div className="dtc-card-icon">
                  <Icon name="acquisition" size={22} />
                </div>
                <h3 className="dtc-card-title">Profitable Customer Acquisition</h3>
                <p className="dtc-card-text">Paid search, paid social, and affiliate at scale, with the ROAS discipline to know when to push and when to pull back.</p>
              </div>
              <div className="dtc-card">
                <div className="dtc-card-icon">
                  <Icon name="funnel" size={22} />
                </div>
                <h3 className="dtc-card-title">Full-Funnel Attribution</h3>
                <p className="dtc-card-text">GA4, server-side tracking, and custom attribution models that give you a single source of truth across every channel.</p>
              </div>
              <div className="dtc-card">
                <div className="dtc-card-icon">
                  <Icon name="revenue" size={22} />
                </div>
                <h3 className="dtc-card-title">E-Commerce Revenue Optimization</h3>
                <p className="dtc-card-text">CRO, LTV/CAC management, and Shopify Plus optimization. I treat your store like a P&amp;L, not a landing page.</p>
              </div>
              <div className="dtc-card">
                <div className="dtc-card-icon">
                  <Icon name="marketplace" size={22} />
                </div>
                <h3 className="dtc-card-title">Marketplace &amp; DTC Hybrid</h3>
                <p className="dtc-card-text">Experience scaling brands across owned DTC channels and third-party marketplaces, without cannibalizing either.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Results That Matter Section */}
        <section className="results-section" id="results">
          <div className="container">
            <h2 className="section-title">Results That Matter</h2>
            <p className="section-subtitle">Selected outcomes from recent engagements. These are the numbers that get discussed in board rooms.</p>
            <div className="results-grid">
              <div className="result-card">
                <div className="result-metric">45%</div>
                <div className="result-label">Increase in ROAS</div>
                <div className="result-brand">SharkNinja ANZ</div>
              </div>
              <div className="result-card">
                <div className="result-metric">52%</div>
                <div className="result-label">Conversion Rate Improvement</div>
                <div className="result-brand">skillshift Consulting Clients</div>
              </div>
              <div className="result-card">
                <div className="result-metric">30%</div>
                <div className="result-label">Online Sales Lift</div>
                <div className="result-brand">Est&eacute;e Lauder</div>
              </div>
              <div className="result-card">
                <div className="result-metric">$50M+</div>
                <div className="result-label">DTC Revenue Growth</div>
                <div className="result-brand">Across DTC Brand Engagements</div>
              </div>
            </div>
          </div>
        </section>

        {/* About/Experience Section */}
        <section className="about" id="about">
          <div className="container">
            <h2 className="section-title">Professional Experience</h2>
            <div className="about-grid">
              <div className="about-image">
                <Image 
                  src="/bemoi_george.jpg" 
                  alt="Bemoi George" 
                  width={400} 
                  height={500} 
                  className="about-img"
                />
              </div>
              <div className="about-content">
                <h3 className="experience-title">My Journey</h3>
                
                <div className="experience-item">
                  <div>
                    <div className="experience-role">Head of Performance Marketing ANZ</div>
                    <div className="experience-company">SharkNinja, Sydney</div>
                  </div>
                  <div className="experience-period">2024 - Current</div>
                </div>
                
                <div className="experience-item">
                  <div>
                    <div className="experience-role">Founder & Managing Director</div>
                    <div className="experience-company">skillshift, Sydney</div>
                  </div>
                  <div className="experience-period">2022 - Current</div>
                </div>
                
                {/* Add more experience items as needed */}
                <div className="experience-item">
                  <div>
                    <div className="experience-role">Head of Growth Marketing & SEO</div>
                    <div className="experience-company">eBay (Gumtree Group), Sydney</div>
                  </div>
                  <div className="experience-period">2022 - 2023</div>
                </div>
                
                <div className="experience-item">
                  <div>
                    <div className="experience-role">Digital Optimization Lead, Global</div>
                    <div className="experience-company">humm Group, Sydney</div>
                  </div>
                  <div className="experience-period">2021 - 2022</div>
                </div>
                
                <div className="experience-item">
                  <div>
                    <div className="experience-role">Experience Director & National SEO Lead</div>
                    <div className="experience-company">Resolution Digital, Sydney</div>
                  </div>
                  <div className="experience-period">2016 - 2021</div>
                </div>

                <div className="experience-item">
                  <div>
                    <div className="experience-role">Senior SEO Specialist MENA</div>
                    <div className="experience-company">Resolution Media, Dubai</div>
                  </div>
                  <div className="experience-period">2014 - 2016</div>
                </div>

                <div className="experience-item">
                  <div>
                    <div className="experience-role">Digital Media Manager ME</div>
                    <div className="experience-company">OT Ventures, Cairo</div>
                  </div>
                  <div className="experience-period">2012 - 2014</div>
                </div>

                <div className="experience-item">
                  <div>
                    <div className="experience-role">Digital Media Specialist MENA</div>
                    <div className="experience-company">Carat, Cairo</div>
                  </div>
                  <div className="experience-period">2009 - 2012</div>
                </div>

                <div className="experience-item">
                  <div>
                    <div className="experience-role">Digital Media Specialist</div>
                    <div className="experience-company">MediaHub, Cairo</div>
                  </div>
                  <div className="experience-period">2006 - 2009</div>
                </div>

                <div className="experience-item">
                  <div>
                    <div className="experience-role">Web Designer & Developer</div>
                    <div className="experience-company">B Studio, Cairo</div>
                  </div>
                  <div className="experience-period">2000 - 2006</div>
                </div>


              </div>
            </div>
          </div>
        </section>

        {/* Brands Section */}
        <section className="brands" id="brands">
          <div className="container">
            <h2 className="section-title">Brands I&apos;ve Driven Growth For</h2>
            <div className="brands-intro">
              <p>A selection of DTC, marketplace, and enterprise brands I&apos;ve delivered measurable commercial outcomes for. Select any brand to see it in context on my experience page.</p>
            </div>
            <div className="brands-grid">
              {loadingBrands ? (
                <p>Loading brands...</p>
              ) : brands.map((brand, index) => (
                <Link 
                  href={`/experience#brand-${brand.slug}`}
                  key={brand.slug || index}
                  className={styles.brandCardLink}
                >
                  <div className={styles.brandCard}>
                    <div className={styles.brandCardLogo}>
                      <BrandLogo
                        logoUrl={brand.logo?.fields?.file?.url ? formatContentfulImageUrl(brand.logo.fields.file.url) : undefined}
                        domain={brand.domain}
                        alt={brand.name || 'Brand logo'}
                        width={48}
                        height={48}
                      />
                    </div>
                    <div className={styles.brandCardInfo}>
                      <span className={styles.brandCardName}>{brand.name}</span>
                      <span className={styles.brandCardMeta}>{brand.yearWorked}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="contact" id="contact">
          <div className="container">
            <h2 className="section-title">Let&apos;s talk</h2>
            <div className="contact-grid">
              <div className="contact-info">
                <h3>Let&apos;s talk about scaling your brand.</h3>
                <p className="contact-text">Whether you&apos;re a DTC brand looking to profitably scale acquisition, an enterprise rethinking its digital P&amp;L, or an agency that needs senior firepower, I&apos;d like to hear what you&apos;re building.</p>
              </div>
              <div className="contact-form">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
